function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { withRouter } from "react-router";
import { CssContainer, Container } from "../../components/container";
import { PageHeader, Ribbon, Card, Pill, Empty, Search, Filters } from "../../components/analytics-kit";
import Error from "../../components/error";
import { Loader } from "../../components/loaders";
import { KpiStrip, Kpi } from "../../components/card";
import { hasTenantFeature, adBillingMode, hasCampaignDashboards, campaignsDashboardOnly } from "../../functions/tenant-features";
import { getGlobalRange, getGlobalComp, subscribePeriod, unsubscribePeriod } from "../../functions/period-store";
import { Chart, ChartSeries, ChartSeriesItem, ChartCategoryAxis, ChartCategoryAxisItem, ChartValueAxis, ChartValueAxisItem, ChartLegend, ChartTooltip } from "@progress/kendo-react-charts";
import MonthsChart from "./months-chart";
import ChartBoundary from "../market-intelligence/chart-boundary";
import { adSvc, channelLabel, num, usd, pct, rangeQs, isActiveStatus, setCampaignStatus } from "./common";
import CampaignsNav, { DeltaTag, kpiDelta, campaignTitle, ribbonDelta } from "./nav";
// Global comparison (top-right "Trend Comparison") → dashboard compare token
function compareOf(comp) {
  return comp === "prev_year" ? "previous_year" : comp === "none" ? "none" : "previous_period";
}

/*
 * Campaigns → Markets = market performance (CAMPAIGNS_DASHBOARD_REDESIGN.md §3.2).
 * The legacy Looker "Location Performance" page, done right: one market, its
 * KPIs + Δ, channel breakdown, ONE campaign leaderboard with a metric toggle,
 * daily trend, landing-page engagement (UTM), and the flights targeting it.
 *   GET /v1/campaign/ad/markets?range&compare            (picker)
 *   GET /v1/campaign/ad/markets/{name}?range&compare     (the page)
 */

const CHANNEL_COLORS = {
  Display: "var(--plk-chart-display)",
  Retarget: "var(--plk-chart-retarget)",
  Search: "var(--plk-chart-search)",
  Social: "var(--plk-chart-social)",
  Video: "var(--plk-chart-video)",
  YouTube: "var(--plk-chart-youtube)",
  "CTV/OTT": "var(--plk-chart-ctv)",
  Pinterest: "var(--plk-chart-pinterest)"
};
const LEADER_METRICS = [["impressions", "Impressions"], ["clicks", "Clicks"], ["ctr", "CTR"], ["videoViews", "Video views"], ["spend", "Spend"], ["conversions", "Conversions"]];
const VIDEO = ["YouTube", "CTV/OTT"];
class MarketsView extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "loadAll", async () => {
      this.setState({
        loading: true,
        error: null
      });
      try {
        const d = await adSvc(`/markets?${rangeQs(this.state.range)}&compare=${this.state.compare}`).getAsync();
        const markets = d && d.markets || [];
        let market = this.state.market;
        if (!market && markets.length) market = markets[0].market;
        if (this._alive) this.setState({
          markets,
          market
        }, this.loadMarket);
      } catch (e) {
        if (this._alive) this.setState({
          error: e && e.message || "Could not load markets.",
          loading: false
        });
      }
    });
    _defineProperty(this, "loadMarket", async () => {
      const {
        market
      } = this.state;
      if (!market) {
        this.setState({
          data: null,
          loading: false
        });
        return;
      }
      this.setState({
        loading: true
      });
      try {
        const d = await adSvc(`/markets/${encodeURIComponent(market)}?${rangeQs(this.state.range)}&compare=${this.state.compare}`).getAsync();
        if (this._alive) this.setState({
          data: d,
          loading: false
        });
      } catch (e) {
        if (this._alive) this.setState({
          error: e && e.message || "Could not load the market.",
          loading: false
        });
      }
    });
    _defineProperty(this, "pick", m => {
      this.props.history.replace(`/Campaigns/Markets/${encodeURIComponent(m)}`);
      this.setState({
        market: m
      }, this.loadMarket);
    });
    _defineProperty(this, "toggle", async c => {
      const next = isActiveStatus(c.status) ? "Paused" : "Enabled";
      try {
        await setCampaignStatus(c.id, next);
        this.loadMarket();
      } catch (e) {/* keep */}
    });
    this.state = {
      markets: null,
      data: null,
      error: null,
      loading: true,
      market: decodeURIComponent(props.params && props.params.market || ""),
      range: getGlobalRange() || "this_month",
      compare: compareOf(getGlobalComp()),
      metric: "impressions"
    };
  }
  componentDidMount() {
    this._alive = true;
    this._sub = subscribePeriod && subscribePeriod(({
      range,
      comp
    }) => this._alive && this.setState({
      range: range || this.state.range,
      compare: compareOf(comp)
    }, () => {
      this.loadAll();
      if (this.loadInsights) this.loadInsights();
    }));
    this.loadAll();
  }
  componentWillUnmount() {
    this._alive = false;
    if (this._sub && unsubscribePeriod) unsubscribePeriod(this._sub);
  }
  componentDidUpdate(prev) {
    const m = decodeURIComponent(this.props.params && this.props.params.market || "");
    if (m !== decodeURIComponent(prev.params && prev.params.market || "")) this.setState({
      market: m
    }, this.loadMarket);
  }
  render() {
    if (!hasCampaignDashboards()) return /*#__PURE__*/React.createElement(CssContainer, {
      cls: "margin-squeeze"
    }, /*#__PURE__*/React.createElement("p", {
      className: "module-empty"
    }, "Campaigns isn't configured for this workspace."));
    const {
      markets,
      data,
      error,
      loading,
      market,
      metric
    } = this.state;
    const money = adBillingMode() !== "managed";
    // Read-only dashboard tenants (managed): no drill to campaign/flight
    // detail, no status toggle, no "flights in this market" list.
    const dashOnly = campaignsDashboardOnly();
    const k = data && data.kpis || {};
    const pk = data && data.compareKpis || null;
    const channels = [...new Set((data && data.series || []).flatMap(d => (d.byChannel || []).map(c => c.type)))];
    const series = (data && data.series || []).map(d => {
      const row = {
        date: d.date.slice(5)
      };
      channels.forEach(c => {
        const m = (d.byChannel || []).find(x => x.type === c);
        row[c] = m ? Number(m.impressions) || 0 : 0;
      });
      return row;
    });
    const metricVal = c => Number((c.kpis || {})[metric]) || 0;
    const rawRows = [...(data && data.campaigns || [])];
    const rows = rawRows.sort((a, b) => metricVal(b) - metricVal(a) || (Number((b.kpis || {}).impressions) || 0) - (Number((a.kpis || {}).impressions) || 0));
    const maxV = Math.max(1, ...rows.map(metricVal));
    const metricAllZero = rows.length > 0 && rows.every(c => metricVal(c) === 0);
    const metricLabel = (LEADER_METRICS.find(([m]) => m === metric) || [, ""])[1];
    // The ranked metric already has its own bar column; don't repeat it as a plain one ("IMPRESSIONS" + "IMPR.").
    const also = m => m !== metric;
    const fmt = (m, v, type) => m === "ctr" ? VIDEO.includes(type) ? "—" : pct(v) : m === "spend" ? usd(v) : m === "clicks" && VIDEO.includes(type) ? "—" : num(v);
    const eng = data && data.engagement || [];
    const engTotals = eng.reduce((a, r) => ({
      users: a.users + r.users,
      sessions: a.sessions + r.sessions,
      engagements: a.engagements + r.engagements,
      conversions: a.conversions + r.conversions
    }), {
      users: 0,
      sessions: 0,
      engagements: 0,
      conversions: 0
    });
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view campaigns-view wa-view"
    }, /*#__PURE__*/React.createElement(CampaignsNav, {
      active: "markets",
      history: this.props.history,
      subtitle: `Per-market delivery and what it drove on the site${market ? ` · ${market}` : ""}${data && data.compare ? ` · vs ${data.compare.from} – ${data.compare.to}` : ""}`,
      chips: [markets && markets.length ? {
        k: "Markets",
        v: num(markets.length)
      } : null]
    }), error && /*#__PURE__*/React.createElement(Error, {
      message: error
    }), markets === null && /*#__PURE__*/React.createElement(Loader, null), markets && markets.length === 0 && /*#__PURE__*/React.createElement(CssContainer, {
      cls: "margin-squeeze"
    }, /*#__PURE__*/React.createElement("p", {
      className: "module-empty"
    }, "No market has delivered in this range yet.")), markets && markets.length > 0 && /*#__PURE__*/React.createElement(CssContainer, {
      cls: "margin-squeeze"
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: 6,
        flexWrap: "wrap",
        alignItems: "center",
        margin: "6px 0 16px"
      }
    }, /*#__PURE__*/React.createElement("span", {
      className: "territory-hint",
      style: {
        margin: 0
      }
    }, "Market"), /*#__PURE__*/React.createElement("span", {
      className: "seg"
    }, markets.map(m => /*#__PURE__*/React.createElement("button", {
      key: m.market,
      className: market === m.market ? "on" : "",
      onClick: () => this.pick(m.market)
    }, m.market, " ", /*#__PURE__*/React.createElement("span", {
      style: {
        opacity: .7
      }
    }, num(m.kpis.impressions))))))), loading && market && !data && /*#__PURE__*/React.createElement(Loader, null), data && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Ribbon, {
      comparing: pk ? true : false,
      tiles: [{
        l: "Impressions",
        v: num(k.impressions),
        d: ribbonDelta(k.impressions, pk && pk.impressions),
        s: Number(k.videoViews) > 0 ? `${num(k.videoViews)} video views` : null,
        sp: series.map(r => channels.reduce((a, c) => a + (Number(r[c]) || 0), 0))
      }, {
        l: "Clicks",
        v: num(k.clicks),
        d: ribbonDelta(k.clicks, pk && pk.clicks)
      }, {
        l: "CTR",
        v: pct(k.ctr),
        d: ribbonDelta(k.ctr, pk && pk.ctr, {
          pp: true
        }),
        s: "click inventory only"
      }, money ? {
        l: "Spend",
        v: usd(k.spend),
        d: ribbonDelta(k.spend, pk && pk.spend),
        invert: true
      } : {
        l: "Video views",
        v: num(k.videoViews),
        d: ribbonDelta(k.videoViews, pk && pk.videoViews)
      }, {
        l: "Conversions",
        v: num(k.conversions),
        d: ribbonDelta(k.conversions, pk && pk.conversions)
      }, {
        l: "Site sessions",
        v: num(engTotals.sessions),
        d: null,
        s: `${num(engTotals.users)} users · ${num(engTotals.conversions)} conversions on site`
      }]
    }), /*#__PURE__*/React.createElement("div", {
      className: "campaign-grid one-one"
    }, /*#__PURE__*/React.createElement("div", {
      className: "campaign-grid-cell"
    }, /*#__PURE__*/React.createElement(Card, {
      title: "Channel breakdown"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Channel"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Camp."), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Impr."), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Clicks"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "CTR"), money && /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Spend"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Conv"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "\u0394 impr."))), /*#__PURE__*/React.createElement("tbody", null, (data.byChannel || []).map(c => /*#__PURE__*/React.createElement("tr", {
      key: c.type
    }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
      className: "code",
      style: {
        background: CHANNEL_COLORS[c.type] || "var(--plk-chart-stable)",
        color: "#fff",
        padding: "1px 8px",
        borderRadius: 999
      }
    }, channelLabel(c.type))), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, c.kpis.campaigns), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(c.kpis.impressions)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, VIDEO.includes(c.type) ? "—" : num(c.kpis.clicks)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, VIDEO.includes(c.type) ? "—" : pct(c.kpis.ctr)), money && /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, usd(c.kpis.spend)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(c.kpis.conversions)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, /*#__PURE__*/React.createElement(DeltaTag, {
      cur: c.kpis.impressions,
      prev: c.compare && c.compare.impressions
    })))))))), /*#__PURE__*/React.createElement("div", {
      className: "campaign-grid-cell"
    }, /*#__PURE__*/React.createElement(Card, {
      title: "Trend",
      hint: "impressions \xB7 daily \xB7 stacked by channel"
    }, /*#__PURE__*/React.createElement(ChartBoundary, null, /*#__PURE__*/React.createElement(Chart, {
      style: {
        height: 220
      },
      transitions: false
    }, /*#__PURE__*/React.createElement(ChartLegend, {
      position: "bottom"
    }), /*#__PURE__*/React.createElement(ChartTooltip, {
      visible: true
    }), /*#__PURE__*/React.createElement(ChartCategoryAxis, null, /*#__PURE__*/React.createElement(ChartCategoryAxisItem, {
      categories: series.map(s => s.date),
      labels: {
        step: Math.max(1, Math.ceil(series.length / 10))
      },
      majorGridLines: {
        visible: false
      }
    })), /*#__PURE__*/React.createElement(ChartValueAxis, null, /*#__PURE__*/React.createElement(ChartValueAxisItem, {
      labels: {
        format: "{0:n0}"
      }
    })), /*#__PURE__*/React.createElement(ChartSeries, null, channels.map(c => /*#__PURE__*/React.createElement(ChartSeriesItem, {
      key: c,
      type: "area",
      stack: true,
      name: channelLabel(c),
      data: series.map(s => s[c]),
      color: CHANNEL_COLORS[c] || "var(--plk-chart-stable)",
      opacity: 0.85,
      markers: {
        visible: false
      }
    })))))))), /*#__PURE__*/React.createElement(MonthsChart, {
      months: data.months,
      money: money,
      title: `By month · ${market}`,
      height: 220
    }), /*#__PURE__*/React.createElement("div", {
      className: "campaign-grid"
    }, /*#__PURE__*/React.createElement("div", {
      className: "campaign-grid-cell"
    }, /*#__PURE__*/React.createElement(Card, {
      title: "Campaign leaderboard",
      right: /*#__PURE__*/React.createElement("span", {
        className: "seg"
      }, LEADER_METRICS.filter(([m]) => money || m !== "spend").map(([m, l]) => /*#__PURE__*/React.createElement("button", {
        key: m,
        className: metric === m ? "on" : "",
        onClick: () => this.setState({
          metric: m
        })
      }, l)))
    }, metricAllZero && /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        margin: "0 0 8px"
      }
    }, "No ", metricLabel.toLowerCase(), " recorded for this market in this range, so the list is ranked by impressions."), /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Campaign"), /*#__PURE__*/React.createElement("th", null, "Channel"), /*#__PURE__*/React.createElement("th", null, "Status"), /*#__PURE__*/React.createElement("th", {
      className: "n",
      style: {
        minWidth: 220
      }
    }, LEADER_METRICS.find(([m]) => m === metric)[1]), also("impressions") && /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Impr."), also("clicks") && /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Clicks"), also("ctr") && /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "CTR"), money && also("spend") && /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Spend"), also("conversions") && /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Conv"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "\u0394"))), /*#__PURE__*/React.createElement("tbody", null, rows.map((c, ri) => {
      const v = metricVal(c);
      return /*#__PURE__*/React.createElement("tr", {
        key: `${c.id || 0}-${c.type}-${c.market}-${c.statsName || c.name}-${ri}`
      }, /*#__PURE__*/React.createElement("td", null, dashOnly ? /*#__PURE__*/React.createElement("span", {
        className: "name"
      }, campaignTitle(c)) : /*#__PURE__*/React.createElement("a", {
        style: {
          cursor: "pointer"
        },
        onClick: () => c.id && this.props.history.push(`/Campaigns/detail/${c.id}`)
      }, campaignTitle(c))), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
        className: "code"
      }, channelLabel(c.type))), /*#__PURE__*/React.createElement("td", null, c.status ? /*#__PURE__*/React.createElement("span", {
        className: "cust-stage",
        style: {
          background: isActiveStatus(c.status) ? "#e5f3ec" : "#eef0f3",
          color: isActiveStatus(c.status) ? "#217a4b" : "#8a94a6",
          cursor: dashOnly ? "default" : "pointer"
        },
        title: dashOnly ? undefined : "Toggle",
        onClick: dashOnly ? undefined : () => this.toggle(c)
      }, isActiveStatus(c.status) ? "Active" : c.status) : "—"), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, /*#__PURE__*/React.createElement("span", {
        style: {
          display: "inline-block",
          height: 10,
          width: Math.round(140 * v / maxV),
          background: CHANNEL_COLORS[c.type] || "var(--plk-chart-stable)",
          borderRadius: 2,
          verticalAlign: "middle",
          marginRight: 8
        }
      }), fmt(metric, v, c.type)), also("impressions") && /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, num(c.kpis.impressions)), also("clicks") && /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, VIDEO.includes(c.type) ? "—" : num(c.kpis.clicks)), also("ctr") && /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, VIDEO.includes(c.type) ? "—" : pct(c.kpis.ctr)), money && also("spend") && /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, usd(c.kpis.spend)), also("conversions") && /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, num(c.kpis.conversions)), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, /*#__PURE__*/React.createElement(DeltaTag, {
        cur: c.kpis.impressions,
        prev: c.compare && c.compare.impressions
      })));
    })))))), /*#__PURE__*/React.createElement("div", {
      className: "campaign-grid one-one"
    }, /*#__PURE__*/React.createElement("div", {
      className: "campaign-grid-cell"
    }, /*#__PURE__*/React.createElement(Card, {
      title: "What people did on the site",
      hint: "landing-page traffic attributed by UTM campaign"
    }, eng.length === 0 ? /*#__PURE__*/React.createElement("p", {
      className: "territory-hint"
    }, "No UTM-attributed site traffic for this market's campaigns in the range.") : /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Campaign"), /*#__PURE__*/React.createElement("th", null, "Source"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Users"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Sessions"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Engagements"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Conv"))), /*#__PURE__*/React.createElement("tbody", null, eng.map((r, i) => /*#__PURE__*/React.createElement("tr", {
      key: i
    }, /*#__PURE__*/React.createElement("td", null, campaignTitle({
      name: r.campaign
    })), /*#__PURE__*/React.createElement("td", {
      className: "code"
    }, r.source || "—"), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(r.users)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(r.sessions)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(r.engagements)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(r.conversions)))))))), !dashOnly && /*#__PURE__*/React.createElement("div", {
      className: "campaign-grid-cell"
    }, /*#__PURE__*/React.createElement(Card, {
      title: "Flights in this market"
    }, (data.flights || []).length === 0 ? /*#__PURE__*/React.createElement("p", {
      className: "territory-hint"
    }, "No flights target this market.") : /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Flight"), /*#__PURE__*/React.createElement("th", null, "Status"), /*#__PURE__*/React.createElement("th", null, "Campaigns"), /*#__PURE__*/React.createElement("th", null, (data.flights[0] || {}).derived ? "Delivered" : "Schedule"))), /*#__PURE__*/React.createElement("tbody", null, data.flights.map((f, i) => /*#__PURE__*/React.createElement("tr", {
      key: f.id || i,
      className: "clickable",
      onClick: () => f.id ? this.props.history.push(`/Campaigns/flight/${f.id}`) : this.props.history.push(`/Campaigns/Flights?q=${encodeURIComponent(f.name)}`)
    }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
      className: "name"
    }, f.name)), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
      className: "cust-stage",
      style: {
        background: /running|active/i.test(f.status) ? "#e5f3ec" : "#eef0f3",
        color: /running|active/i.test(f.status) ? "#217a4b" : "#55606e"
      }
    }, f.status)), /*#__PURE__*/React.createElement("td", null, f.activeCells, " of ", f.cells, " active"), /*#__PURE__*/React.createElement("td", {
      className: "code"
    }, f.startDate ? new Date(f.startDate).toLocaleDateString() : "—", " \u2192 ", f.endDate ? new Date(f.endDate).toLocaleDateString() : f.derived ? "—" : "no end"))))))))));
  }
}
const Routable = withRouter(MarketsView);
export default class CampaignMarketsViewComponent extends React.Component {
  render() {
    return /*#__PURE__*/React.createElement(Routable, {
      params: this.props.params
    });
  }
}